import { themeGet } from '@styled-system/theme-get';
import React from 'react';
import styled, { css } from 'styled-components';
import { borderRadius, boxShadow, compose, fontSize, height, space, width } from 'styled-system';
import { Flex } from '../Flex/Flex';
import { boxShadowAttrs } from '../utils/attrs/boxShadowAttrs';
import { applySizes, applyVariations, borders, getPaletteColor, getTextColorOn } from '../utils/utils';
/**
 * @public
 */
export const borderRadiusButtonValues = ['none', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', 'full'];
const isValidBorderRadius = size => size && borderRadiusButtonValues.includes(size);
const sizes = {
  small: css(["border-radius:", ";font-size:", "px;padding:7px 12px;"], props => themeGet(`borderRadii.${isValidBorderRadius(props.borderRadius) ? props.borderRadius : 'action-sm'}`)(props), themeGet('fontSizes.0')),
  medium: css(["border-radius:", ";font-size:", "px;padding:9.5px 18px;"], props => themeGet(`borderRadii.${isValidBorderRadius(props.borderRadius) ? props.borderRadius : 'action-md'}`)(props), themeGet('fontSizes.1')),
  large: css(["border-radius:", ";font-size:", "px;padding:12px 22px;"], props => themeGet(`borderRadii.${isValidBorderRadius(props.borderRadius) ? props.borderRadius : 'action-lg'}`)(props), themeGet('fontSizes.2')),
  extraLarge: css(["border-radius:", ";font-size:", "px;padding:16px 22px;"], props => themeGet(`borderRadii.${isValidBorderRadius(props.borderRadius) ? props.borderRadius : 'action-xl'}`)(props), themeGet('fontSizes.2'))
};
const variations = {
  fill: css(["background-color:", ";color:", ";&:hover{background-color:", ";", "}&:focus-visible{outline:", ";box-shadow:", ";}"], props => getPaletteColor(props.disabled ? 'light' : 'base')(props), props => getTextColorOn(props.disabled ? 'light' : 'base')(props), props => getPaletteColor(props.disabled ? 'light' : 'dark')(props), props => props.disabled ? '' : `color: ${getTextColorOn('dark')(props)};`, props => `0px solid ${getPaletteColor(props.disabled ? '' : 'dark')(props)}`, props => ` 0 0 0 2px  ${getPaletteColor(props.disabled ? '' : 'dark')(props)}`),
  link: css(["-webkit-font-smoothing:inherit;font-weight:", ";color:", ";line-height:", ";vertical-align:inherit;padding:0;background-color:transparent;&:hover{color:", ";text-decoration:underline;}&&&:disabled{background-color:transparent;}"], props => props.theme.fontWeights.medium, getPaletteColor('base'), props => props.theme.lineHeights.standard, getPaletteColor('dark')),
  outline: css(["color:", ";box-shadow:inset 0 0 0 2px ", ";background-color:transparent;&:hover{background-color:transparent;", "}"], props => getPaletteColor(props.disabled ? 'light' : 'base')(props), props => getPaletteColor(props.disabled ? 'light' : 'base')(props), props => props.disabled ? '' : `
      color: ${getPaletteColor('dark')(props)};
      box-shadow: inset 0 0 0 2px ${getPaletteColor('dark')(props)};
    `),
  plain: css(["background-color:transparent;color:", ";&:hover{background-color:", ";color:", ";}&:focus-visible{outline:0px solid ", ";box-shadow:0 0 0 2px ", ";}"], getPaletteColor('base'), getPaletteColor('background.base'), getPaletteColor('dark'), getPaletteColor('dark'), getPaletteColor('dark')),
  subtle: css(["background-color:", ";color:", ";&:hover{background-color:", ";color:", ";}&:focus-visible{outline:0px solid ", ";box-shadow:0 0 0 2px ", ";}"], getPaletteColor('background.base'), getPaletteColor('base'), getPaletteColor('background.tint'), getPaletteColor('dark'), getPaletteColor('dark'), getPaletteColor('dark')),
  white: css(["background-color:", ";color:", ";&:hover{background-color:", ";color:", ";}&:focus-visible{outline:0px solid ", ";box-shadow:0 0 0 2px ", ";}"], getPaletteColor('background.lightest'), getPaletteColor('base'), getPaletteColor('background.base'), getPaletteColor('dark'), getPaletteColor('dark'), getPaletteColor('dark')),
  lightFill: css(["background-color:", ";color:", ";", " &:focus-visible{outline:", ";box-shadow:", ";}"], getPaletteColor('light'), getPaletteColor('base'), props => props.disabled ? '' : `
      &:hover {
        background-color: ${getPaletteColor('light')(props)};
        color: ${getPaletteColor('dark')(props)};
      }`, props => `0px solid ${getPaletteColor(props.disabled ? '' : 'dark')(props)}`, props => ` 0 0 0 2px  ${getPaletteColor(props.disabled ? '' : 'dark')(props)}`),
  input: css(["appearance:none;background-color:transparent;border-style:solid;border-width:1px;color:", ";display:block;font-family:inherit;font-weight:normal;line-height:normal;margin:0;padding:14px 12px;text-align:left;width:100%;&:disabled{background-color:", ";color:", ";}", " ", ""], getPaletteColor('text.base'), getPaletteColor('background.light'), getPaletteColor('text.light'), props => borders({
    ...props,
    color: undefined
  }), props => compose(space, fontSize, borderRadius)(props))
};
export const buttonStyles = /*#__PURE__*/css(["-webkit-font-smoothing:antialiased;display:inline-block;vertical-align:middle;text-align:center;text-decoration:none;font-family:inherit;font-weight:", ";line-height:1.5;cursor:", ";border-radius:", ";border-width:0;border-style:solid;&:disabled{cursor:not-allowed;color:", ";background-color:", ";}", ";", ";"], props => props.theme.fontWeights.bold, props => props.disabled ? 'default' : 'pointer', props => themeGet(`borderRadii.${isValidBorderRadius(props.borderRadius) ? props.borderRadius : 'action-md'}`)(props), getPaletteColor('background.tone'), getPaletteColor('background.base'), ({
  theme
}) => applySizes(sizes, 'medium', theme.mediaQueries), applyVariations('Button', variations));
const iconButtonPaddings = {
  IconLeft: {
    small: {
      p: '6px 14px 6px 10px'
    },
    medium: {
      p: '8px 20px 8px 16px'
    },
    large: {
      p: '12px 24px 12px 20px'
    },
    extraLarge: {
      p: '16px 24px 16px 20px'
    }
  },
  IconRight: {
    small: {
      p: '6px 10px 6px 14px'
    },
    medium: {
      p: '8px 16px 8px 20px'
    },
    large: {
      p: '12px 20px 12px 24px'
    },
    extraLarge: {
      p: '16px 20px 16px 24px'
    }
  },
  both: {
    small: {
      p: '6px 10px'
    },
    medium: {
      p: '8px 16px'
    },
    large: {
      p: '12px 20px'
    },
    extraLarge: {
      p: '16px 20px'
    }
  }
};
const iconOnlyButtonPaddings = {
  small: {
    px: '6px',
    py: '6px'
  },
  medium: {
    px: '8px',
    py: '8px'
  },
  large: {
    px: '12px',
    py: '12px'
  },
  extraLarge: {
    px: '16px',
    py: '16px'
  }
};
const iconButtonSizes = {
  small: 20,
  medium: 24,
  large: 24,
  extraLarge: 24
};
// eslint-disable-next-line @typescript-eslint/naming-convention
function getPaddingProps({
  IconLeft,
  IconRight,
  size,
  variation,
  hasChildren
}) {
  const hasIcon = !!(IconLeft || IconRight);
  const sizeIndex = Array.isArray(size) ? size[0] : size;
  if (variation === 'link') {
    return {
      p: 0
    };
  }
  if (hasIcon) {
    if (hasChildren) {
      if (IconLeft && IconRight) {
        return iconButtonPaddings.both[sizeIndex];
      } else if (IconLeft) {
        return iconButtonPaddings.IconLeft[sizeIndex];
      } else if (IconRight) {
        return iconButtonPaddings.IconRight[sizeIndex];
      }
      return {};
    } else {
      return iconOnlyButtonPaddings?.[sizeIndex] ?? {};
    }
  }
  return {};
}
/**
 * Use the <Button /> component to render a primitive button. Use the `variation` prop to change the look of the button.
 */
const StyledButton = /*#__PURE__*/styled.button.attrs(props => {
  const {
    width,
    height,
    title,
    'aria-label': ariaLabel,
    borderRadius,
    IconLeft,
    IconRight,
    size,
    variation,
    hasChildren
  } = props;
  const paddingProps = getPaddingProps({
    IconLeft,
    IconRight,
    size,
    variation,
    hasChildren
  });
  return {
    borderRadius,
    ...boxShadowAttrs(props),
    width,
    height,
    'aria-label': ariaLabel || title,
    ...paddingProps
  };
}).withConfig({
  displayName: "Button__StyledButton",
  componentId: "sc-uq13ez-0"
})(["", " ", ""], buttonStyles, props => compose(width, height, space, boxShadow)(props));
// eslint-disable-next-line @typescript-eslint/naming-convention
const ButtonIcon = ({
  Component,
  ...props
}) => {
  return Component ? React.createElement(Component, {
    ...props
  }) : null;
};
/**
 * @public
 */
export const Button = React.forwardRef((props, ref) => {
  const {
    children,
    ...restProps
  } = props;
  const {
    IconLeft,
    IconRight,
    size = 'medium',
    flexProps = {}
  } = props;
  const hasChildren = React.Children.toArray(children).length > 0;
  const sizeIndex = Array.isArray(size) ? size[0] : size;
  const iconSize = iconButtonSizes?.[sizeIndex] ?? 24;
  return React.createElement(StyledButton, {
    ...restProps,
    hasChildren: hasChildren,
    ref: ref
  }, React.createElement(Flex, {
    alignItems: 'center',
    justifyContent: 'center',
    ...flexProps
  }, React.createElement(ButtonIcon, {
    Component: IconLeft,
    size: iconSize,
    mr: children ? 2 : 0
  }), children, React.createElement(ButtonIcon, {
    Component: IconRight,
    size: iconSize,
    ml: children ? 2 : 0
  })));
});
Button.defaultProps = {
  color: 'primary',
  size: 'medium',
  variation: 'fill'
};
Button.displayName = 'Button';